import React, { useContext } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import {
  Button,
  InputLabel,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import {
  notRequiredPhoneNumberValidation,
  required,
  zipCodeValidation,
} from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useCloseAndReset } from "hooks/useCloseAndReset";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormSelectField } from "UI/FormSelectField";
import { states } from "models/states";
import { trimAllStringValuesInObject } from "utils/helpers";

export const EditLocationDialog = ({
  location,
  handleClose,
  refreshList,
  recipientId,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning } = useContext(GlobalContext);
  const isDisabled = useAppSelector((state) => !!state.spinner);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({
    values: location,
  });

  const forceCloseAndReset = () => {
    reset();
    handleClose();
  };

  const handleDelete = createApiCall(async () => {
    const { id } = getValues();
    const result = await API.del("restApi", `/locations/${id}`, {});

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Location deleted successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    forceCloseAndReset();
  });

  const onSubmit = createApiCall(async () => {
    const location = getValues();
    location.practiceId = recipientId;
    const result = await API.post("restApi", "/locations", {
      body: {
        location: trimAllStringValuesInObject(location),
        action: "update",
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Location updated successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    forceCloseAndReset();
  });

  const closeAndReset = useCloseAndReset(forceCloseAndReset, isDirty);

  return (
    <DialogWindow
      open={!!location}
      withCloseButton
      handleClose={closeAndReset}
      title={"Edit Location"}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack mt={1} gap={2}>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="name">Location name</InputLabel>
              <FormTextField
                id="name"
                name="name"
                control={control}
                rules={{ required }}
              />
            </Stack>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="speciality">
                Speciality (optional)
              </InputLabel>
              <FormTextField
                id="speciality"
                name="speciality"
                control={control}
              />
            </Stack>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="phone">Phone (optional)</InputLabel>
              <FormMaskedTextField
                id="phone"
                name="phone"
                control={control}
                rules={notRequiredPhoneNumberValidation}
                mask="999-999-9999"
                InputProps={{
                  autoComplete: "off",
                }}
              />
            </Stack>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="street">Address Line 1</InputLabel>
              <FormTextField
                id="street"
                name="street"
                control={control}
                rules={{ required }}
                InputProps={{
                  autoComplete: "off",
                  disabled: isDisabled,
                }}
              />
            </Stack>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="street22">
                Address Line 2 {"(optional)"}
              </InputLabel>
              <FormTextField
                id="stree2"
                name="street2"
                control={control}
                InputProps={{
                  autoComplete: "off",
                  disabled: isDisabled,
                }}
              />
            </Stack>
            <Stack direction={isMobile ? "column" : "row"} gap={2}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="city">City</InputLabel>
                <FormTextField
                  id="city"
                  name="city"
                  control={control}
                  rules={{ required }}
                  InputProps={{
                    autoComplete: "off",
                    disabled: isDisabled,
                  }}
                />
              </Stack>
              <Stack
                alignItems={"flex-start"}
                minWidth={"30%"}
                maxWidth={"35%"}
              >
                <InputLabel htmlFor="state">State</InputLabel>
                <FormSelectField
                  id="state"
                  name="state"
                  control={control}
                  options={states}
                  rules={{ required }}
                  withNone={false}
                  InputProps={{
                    disabled: isDisabled,
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="zipCode">Zip Code</InputLabel>
                <FormTextField
                  id="zipCode"
                  name="zipCode"
                  control={control}
                  rules={zipCodeValidation}
                  InputProps={{
                    autoComplete: "off",
                    disabled: isDisabled,
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      }
      actions={
        <Stack
          mt={2}
          mb={2}
          width={"100%"}
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={1}
        >
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete Location
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty}
          >
            Save Changes
          </Button>
        </Stack>
      }
    />
  );
};
